import { LIMIT_MAX } from '@/constants';
import { fetcher } from '@/services/graphql/fetcher/index';
import { UserCouponState, type AnyObj } from '@/types';
import {
  fetchApplyCouponDocument,
  fetchCancelCouponDocument,
  fetchClaimCouponDocumenet,
  fetchGetActiveCouponsDocumenet,
  fetchGetActiveFirstCustomerBenefitDocument,
  fetchGetCouponBundleDocument,
  fetchGetCouponHoldDocument,
  fetchGetCouponOneDocumenet,
  fetchGetReceivedCouponBundlesDocument,
  fetchOrderApplyCouponDocument,
  fetchReceiveCouponBundleDocumenet,
  fetchReceiveFirstCustomerBenefitDocumenet,
  fetchUserCouponDocumenet,
  fetchUserCouponsDocumenet,
  fetchCouponsDocumenet,
} from '../graphql/docs';

/**
 * 获取用户全部优惠卷
 */
export const fetchCouponList = async (isUnused: boolean, ids?: Array<string>) => {
  let state: AnyObj = {};
  let id: AnyObj = {};
  const condition = [UserCouponState.unused, UserCouponState.notStarted];
  if (isUnused) {
    state.in = condition;
  } else {
    state.notIn = condition;
  }
  if (ids && ids.length) {
    id.in = ids
  }
  const res = await fetcher.query(fetchUserCouponsDocumenet, {
    variables: {
      options: {
        take: LIMIT_MAX,
        filter: {
          state,
          id
        },
        sort: {
          maturityAt: 'DESC',
        },
      },
    },
  });
  return res.data.value?.userCoupons?.items;
};

/**
 * 获取用户优惠卷详情
 * @param couponId - 用户优惠券id
 * @returns
 */
export const fetchUserCouponDetail = async (userCouponId: string, removeNewTag = false) => {
  const res = await fetcher.query(fetchUserCouponDocumenet, {
    variables: {
      userCouponId,
      removeNewTag,
    },
  });
  return res.data.value?.userCoupon;
};

/**
 * 获取优惠卷详情
 * @param couponId - 优惠券id
 * @returns
 */
export const fetchCouponDetail = async (couponId: string) => {
  const res = await fetcher.query(fetchGetCouponOneDocumenet, {
    variables: {
      couponId,
    },
  });
  return res.data.value?.getCouponOne;
};

/**
 * 领取优惠券
 * @param couponId - 优惠券id
 */
export const fetchClaimCoupon = async (couponId: string) => {
  const res = await fetcher.mutation(fetchClaimCouponDocumenet, {
    couponId,
  });
  return res.data?.claimCoupon;
};

/**
 * 获取 可用/不可用 优惠券列表
 * @param orderId - 订单id
 */
export const fetchGetActiveCoupons = async (orderId: string) => {
  const res = await fetcher.query(fetchGetActiveCouponsDocumenet, {
    variables: {
      orderId,
    },
  });
  return res.data.value?.getActiveCoupons;
};

/**
 * 使用优惠券优惠
 * @param userCouponId - 优惠券id
 * @param orderId - 订单id
 */
export const fetchApplyCoupon = async (userCouponId: string, orderId: string) => {
  const res = await fetcher.mutation(fetchApplyCouponDocument, {
    userCouponId,
    orderId,
  });
  return res.data?.applyCoupon;
};

/**
 * 取消使用优惠券优惠
 * @param orderId - 订单id
 */
export const fetchCancelCoupon = async (orderId: string) => {
  const res = await fetcher.mutation(fetchCancelCouponDocument, {
    orderId,
  });
  return res.data?.cancelCoupon;
};

/**
 * 获取用户的优惠券持有信息
 * @param couponId - 优惠券id
 */
export const fetchGetCouponHold = async (couponId: string) => {
  const res = await fetcher.query(fetchGetCouponHoldDocument, {
    variables: {
      couponId,
    },
  });
  return res.data.value?.getCouponHold;
};
/**
 * 获取优惠券礼包详信息
 * @param couponBundleId - 活动id
 */
export const fetchGetCouponBundle = async (couponBundleId: string) => {
  const res = await fetcher.query(fetchGetCouponBundleDocument, {
    variables: {
      id: couponBundleId,
    },
  });
  return res.data.value?.couponBundle;
};

/**
 * 领取优惠券礼包
 * @param receiveCouponBundleId - 优惠券礼包id
 */
export const fetchReceiveCouponBundle = async (receiveCouponBundleId: string) => {
  const res = await fetcher.mutation(fetchReceiveCouponBundleDocumenet, {
    id: receiveCouponBundleId,
  });
  return res.data?.receiveCouponBundle;
};

/**
 * 获取已经领取过的优惠券数据
 * @param couponBundleId
 * @returns
 */
export const fetchGetReceivedCouponBundles = async (couponBundleId: string) => {
  const res = await fetcher.query(fetchGetReceivedCouponBundlesDocument, {
    variables: {
      couponBundleId,
    },
  });
  return res.data.value?.getReceivedCouponBundles;
};

/**
 * 获取活跃的新客福利
 */
export const fetchGetActiveFirstCustomerBenefit = async () => {
  const res = await fetcher.query(fetchGetActiveFirstCustomerBenefitDocument, {
    variables: {},
  });
  return res.data.value?.getActiveFirstCustomerBenefit;
};

/**
 * 领取新客福利
 * @param id
 * @returns
 */
export const fetchReceiveFirstCustomerBenefit = async (id: string) => {
  const res = await fetcher.mutation(fetchReceiveFirstCustomerBenefitDocumenet, {
    id,
  });
  return res.data?.receiveFirstCustomerBenefit;
};

/**
 * 订单应用优惠券
 * @param orderId - 订单id
 */
export const fetchOrderApplyCoupon = async (orderId: string) => {
  const res = await fetcher.mutation(fetchOrderApplyCouponDocument, {
    orderId,
  });
  return res.data?.orderApplyCoupon;
};

/**
 * 获取全部优惠卷列表
 * @param ids - 优惠券id
 */
export const fetchCouponAllList = async (ids: Array<string> = []) => {
  const res = await fetcher.query(fetchCouponsDocumenet, {
    variables: {
      couponIds: ids
    },
  });
  
  return res.data.value?.coupons || [];
};
